/**
 * FeedbackWidget — al pie de cada respuesta (uno por respuesta, no también dentro de la tarjeta).
 *
 * 2026-10-20: con producto pregunta "¿Lo vendiste?" (Sí / Vendí otro / No, y si no, por qué: caro,
 * no había, solo consultaba, no servía). Es lo que mide si el asistente ayuda a vender; con 👍/👎
 * hubo 2 votos en 234 consultas. Sin producto (consejo, información): "¿Te sirvió?" Sí / No.
 *
 * Opcional: nunca bloquea seguir. Tap targets ≥44px. Drimian P9 — medir para entender.
 */
import { useState } from 'react'
import { submitFeedback } from '../api/feedback'
import { FeedbackPanel } from './FeedbackPanel'
import { Icon } from './icons'
import type { FeedbackCategory, FeedbackVote } from '../types/feedback'

interface Props {
  trace_id: string | null
  /** 2026-10-01 — id de la consulta en el registro propio (siempre viene del backend). */
  consulta_id?: string | null
  device_id: string
  /** La respuesta trae un producto: se pregunta si se vendió. */
  venta?: boolean
}

type WidgetState =
  | { kind: 'idle' }
  | { kind: 'expanded' } // tras "No"
  | { kind: 'submitting' }
  | { kind: 'submitted'; vote: FeedbackVote }
  | { kind: 'error' }

const MOTIVOS_NO_VENTA: FeedbackCategory[] = ['caro', 'sin_stock', 'solo_consulta', 'no_servia']
const MOTIVOS_NO_SIRVIO: FeedbackCategory[] = ['partial', 'wrong_dose', 'wrong_pa', 'other']

const GRACIAS: Partial<Record<FeedbackVote, string>> = {
  vendido: 'Anotado: vendido',
  otro_producto: 'Anotado: vendió otro',
  no_vendido: 'Anotado, gracias',
  up: 'Gracias',
  down: 'Gracias por avisar',
}

function Boton({ texto, onClick, disabled, principal = false }: {
  texto: string
  onClick: () => void
  disabled: boolean
  principal?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="text-[14px] font-medium disabled:opacity-40"
      style={{
        minHeight: 44,
        padding: '0 16px',
        borderRadius: 999,
        border: `1px solid ${principal ? 'var(--color-ok)' : 'var(--color-border-strong)'}`,
        background: 'var(--color-surface)',
        color: principal ? 'var(--color-ok)' : 'var(--color-text)',
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      {texto}
    </button>
  )
}

export function FeedbackWidget({ trace_id, consulta_id = null, device_id, venta = false }: Props) {
  const [state, setState] = useState<WidgetState>({ kind: 'idle' })

  if (trace_id === null && !consulta_id) {
    return null
  }

  const submit = async (vote: FeedbackVote, category?: FeedbackCategory, comment?: string) => {
    setState({ kind: 'submitting' })
    try {
      await submitFeedback({ consulta_id, trace_id, vote, category, comment, device_id })
      setState({ kind: 'submitted', vote })
    } catch {
      setState({ kind: 'error' })
    }
  }

  if (state.kind === 'submitted') {
    return (
      <div role="status" aria-live="polite" className="text-[13px] mt-2 inline-flex items-center gap-1.5"
        style={{ color: 'var(--color-text-muted)' }}>
        <Icon.Check width={14} height={14} aria-hidden="true" />
        {GRACIAS[state.vote] ?? 'Gracias'}
      </div>
    )
  }

  if (state.kind === 'error') {
    return (
      <div role="alert" className="text-[13px] mt-2 inline-flex items-center gap-1.5"
        style={{ color: 'var(--color-danger)' }}>
        <Icon.Alert width={14} height={14} aria-hidden="true" />
        No se pudo guardar. Probá de nuevo más tarde.
      </div>
    )
  }

  const ocupado = state.kind === 'submitting'

  return (
    <div className="flex flex-col gap-1 mt-2">
      <div role="group" aria-label={venta ? '¿Lo vendiste?' : '¿Te sirvió?'} className="flex flex-wrap items-center gap-2">
        <span className="text-[12px] uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>
          {venta ? '¿Lo vendiste?' : '¿Te sirvió?'}
        </span>
        {venta ? (
          <>
            <Boton texto="Sí" principal onClick={() => submit('vendido')} disabled={ocupado} />
            <Boton texto="Vendí otro" onClick={() => submit('otro_producto')} disabled={ocupado} />
            <Boton texto="No" onClick={() => setState({ kind: 'expanded' })} disabled={ocupado} />
          </>
        ) : (
          <>
            <Boton texto="Sí" principal onClick={() => submit('up')} disabled={ocupado} />
            <Boton texto="No" onClick={() => setState({ kind: 'expanded' })} disabled={ocupado} />
          </>
        )}
      </div>
      {state.kind === 'expanded' && (
        <FeedbackPanel
          titulo={venta ? '¿Por qué no se vendió?' : '¿Qué falló?'}
          categorias={venta ? MOTIVOS_NO_VENTA : MOTIVOS_NO_SIRVIO}
          onSubmit={(category, comment) => submit(venta ? 'no_vendido' : 'down', category, comment)}
          onCancel={() => setState({ kind: 'idle' })}
        />
      )}
    </div>
  )
}
